import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { shouldUpdateValue } from '../helpers';
import TextareaAutosize from 'react-textarea-autosize';

import OpenTextAnswerInput from './OpenTextAnswerInput';
import useSubtaskUpdateAnswerChoice from './hooks/useSubtaskUpdateAnswerChoice';
import useSubtaskUpdate from './hooks/useSubtaskUpdate';
import MultiSelectEditor from './MultiSelectEditor';
import { useTaskStatus } from 'components/projects/taskList/hooks';
import { Button } from '@companycam/slab-web';
import AnswerOption from 'components/projects/taskList/subTasks/AnswerOption';

const messages = defineMessages({
  placeholder: {
    id: 'Project.taskList.subTasks.label.placeholder',
    defaultMessage: 'Description (optional)',
    description: 'Placeholder text for subtask label',
  },
  selectAll: {
    id: 'Project.taskList.subTasks.label.selectAll',
    defaultMessage: '(select all that apply)',
    description: 'Message to select all multi-select subtask options',
  },
});

const Form = styled.form`
  flex: 1;
  margin-right: auto;
  padding: ${(props) =>
    props.isEditingTask ? '0 8px 0 30px' : '0 32px 0 30px'};
`;

const AnswerOptionContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--cc_size_spacing_s);
`;

const AnswerOptionButton = styled(Button)``;

// subtask
const LabelInput = styled(TextareaAutosize)`
  background: transparent;
  border: 0;
  padding: var(--cc_size_spacing_s) 0 var(--cc_size_spacing_xxs);
  color: var(--cc_color_text_subtle);
  font-size: 13px;
  font-weight: 400;
  resize: none;
  width: 100%;
  line-height: 22px;
  cursor: ${({ readOnly }) => (readOnly ? 'default' : 'text')};
`;

const MultiSelectAnswerButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: ${({ selectedAnswer }) =>
    selectedAnswer
      ? 'var(--cc_color_collab_navy)'
      : 'var(--cc_color_button_background_subtle)'};
  color: ${({ selectedAnswer }) =>
    selectedAnswer ? 'white' : 'var(--cc_color_button_text_subtle)'};

  &:hover {
    background-color: ${({ selectedAnswer }) =>
      selectedAnswer
        ? 'var(--cc_color_collab_navy)'
        : 'var(--cc_color_button_background_subtle_darken_10)'};
  }

  &::before {
    content: ${(props) => (props.selectedAnswer ? '"✓"' : '""')};

    position: relative;

    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;

    width: 12px;
    height: 12px;

    background: var(--cc_color_background_1);
    border: 1px solid var(--cc_color_border_input_inactive);
    border-radius: 2px;
    border-width: ${({ selectedAnswer }) => (selectedAnswer ? '0' : '1px')};

    font-family: system-ui, sans-serif;
    font-size: 10px;
    color: ${(props) =>
      props.completed
        ? 'var(--cc_color_utility_success)'
        : 'var(--cc_color_text_default)'};
  }
`;

const SubtaskForm = ({
  listId,
  subtask,
  isTemplate,
  isEditingTask = false,
  updateSubtaskTemplate,
  task,
  hideCompletedTasks,
}) => {
  const { formatMessage } = useIntl();
  const [labelInput, setLabelInput] = useState(subtask.label);
  const updateSubtask = useSubtaskUpdate();
  const updateSubtaskAnswerChoice = useSubtaskUpdateAnswerChoice({
    listId,
  });
  const shouldHideLabelInput = !isEditingTask && !subtask.label.length;

  const { toggleTaskStatus } = useTaskStatus({
    taskId: task.id,
    taskListId: listId,
    skipOptimisticUpdate: hideCompletedTasks,
  });

  const handleUpdateSubtaskTemplate = (updatedCustomAnswers) => {
    updateSubtaskTemplate({
      variables: {
        input: {
          id: subtask.id,
          label: labelInput,
          answerType: subtask.answerType,
          customAnswers: updatedCustomAnswers,
        },
      },
    });
  };

  const handleUpdateSubtask = (updatedCustomAnswers) => {
    if (isTemplate) {
      handleUpdateSubtaskTemplate(updatedCustomAnswers);
    } else {
      updateSubtask({
        variables: {
          input: {
            id: subtask.id,
            label: labelInput,
            answerType: subtask.answerType,
            customAnswers: updatedCustomAnswers,
          },
        },
      });
    }
  };

  const handleOnBlur = (updatedCustomAnswers = subtask.answerOptions) => {
    if (!isEditingTask) return;

    const multiSelectAnswerOptionsChanged = shouldUpdateValue(
      subtask.answerOptions,
      updatedCustomAnswers,
    );

    if (
      shouldUpdateValue(subtask.label, labelInput) ||
      multiSelectAnswerOptionsChanged
    ) {
      handleUpdateSubtask(updatedCustomAnswers);

      if (multiSelectAnswerOptionsChanged && task.completed) {
        // TODO[TaskList]: remove once backend logic can handle this logic
        toggleTaskStatus(task);
      }
    }
  };

  const handleOnChange = (answerChoice) => {
    let answerChoices;
    if (subtask.answerType === 'multi_select') {
      if (subtask.answerChoices?.includes(answerChoice)) {
        answerChoices = subtask.answerChoices.filter(
          (savedAnswer) => savedAnswer !== answerChoice,
        );
      } else {
        answerChoices = subtask.answerChoices?.length
          ? [...subtask.answerChoices, answerChoice]
          : [answerChoice];
      }
    } else {
      answerChoices = subtask.answerChoices?.includes(answerChoice)
        ? []
        : [answerChoice];
    }

    updateSubtaskAnswerChoice({
      variables: {
        input: {
          id: subtask.id,
          answerText: null,
          answerChoices,
        },
      },
    });
  };

  const handleOnKeyDown = (e) => {
    if (e.keyCode === 13) {
      document.activeElement.blur();
    }
  };

  const renderAnswerOptions = () => {
    switch (subtask.answerType) {
      case 'open_text':
        return (
          <OpenTextAnswerInput
            subtask={subtask}
            listId={listId}
            isTemplate={isTemplate}
          />
        );
      case 'multi_select':
        return isEditingTask ? (
          <MultiSelectEditor subtask={subtask} onBlur={handleOnBlur} />
        ) : (
          <AnswerOptionContainer>
            {subtask.answerOptions?.map((answerOption, index) => (
              <AnswerOption
                // eslint-disable-next-line react/no-array-index-key
                key={`${subtask.id}-${answerOption}-${index}`}
                id={`${subtask.id}-${answerOption}-${index}`}
                answerOption={answerOption}
                index={index}
                answerChoices={subtask?.answerChoices}
                onChange={handleOnChange}
                disabled={isTemplate}
                completed={task.completed}
              >
                <MultiSelectAnswerButton
                  forwardedAs="label"
                  size="small"
                  selectedAnswer={subtask.answerChoices?.includes(index)}
                  checked={subtask.answerChoices?.includes(index)}
                  completed={task.completed}
                  htmlFor={`${subtask.id}-${answerOption}-${index}`}
                  aria-label={answerOption}
                >
                  {answerOption}
                </MultiSelectAnswerButton>
              </AnswerOption>
            ))}
          </AnswerOptionContainer>
        );
      case 'yes_no':
      case 'rating':
        return (
          <AnswerOptionContainer>
            {subtask.answerOptions.map((answerOption, index) => (
              <AnswerOption
                // eslint-disable-next-line react/no-array-index-key
                key={`${subtask.id}-${answerOption}-${index}`}
                id={`${subtask.id}-${answerOption}-${index}`}
                answerOption={answerOption}
                index={index}
                answerChoices={subtask?.answerChoices}
                onChange={handleOnChange}
                disabled={isTemplate}
                completed={task.completed}
              >
                <AnswerOptionButton
                  forwardedAs="label"
                  size="small"
                  completed={task.completed}
                  htmlFor={`${subtask.id}-${answerOption}-${index}`}
                  aria-label={answerOption}
                >
                  {answerOption}
                </AnswerOptionButton>
              </AnswerOption>
            ))}
          </AnswerOptionContainer>
        );
      default:
        return null;
    }
  };

  return (
    <Form isEditingTask={isEditingTask} onSubmit={(e) => e.preventDefault()}>
      {shouldHideLabelInput ? null : (
        <LabelInput
          data-testid="subtask-form-label-input"
          onBlur={() => handleOnBlur()}
          onChange={(e) => setLabelInput(e.target.value)}
          onKeyDown={handleOnKeyDown}
          placeholder={formatMessage(messages.placeholder)}
          value={!isEditingTask ? subtask.label : labelInput}
          type="text"
          readOnly={!isEditingTask}
          autoFocus={isEditingTask}
        />
      )}
      {renderAnswerOptions()}
    </Form>
  );
};

SubtaskForm.propTypes = {
  listId: PropTypes.string.isRequired,
  isTemplate: PropTypes.bool.isRequired,
  isEditingTask: PropTypes.bool.isRequired,
  updateSubtaskTemplate: PropTypes.func.isRequired,
  task: PropTypes.shape({
    id: PropTypes.string,
    completed: PropTypes.bool,
  }).isRequired,
  subtask: PropTypes.shape({
    answerChoices: PropTypes.arrayOf(PropTypes.number),
    answerOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
    answerType: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.string,
  }),
  hideCompletedTasks: PropTypes.bool,
};

export default SubtaskForm;
